import { VariantProps } from 'class-variance-authority';
import { ReactNode } from 'react';
import { Button } from '../Button';
import { IconButton } from '../IconButton';
import { CrossIcon } from '../Icons';
import { CREATE_PORTAL_ID } from './constants';
import { modalDialogVariants } from './logic';
import styles from './styles.module.scss';
import { useDisableModalBackScroll } from './useDisableModalBackScroll';
import { useHandleKeyDown } from './useHandleKeyDown';

type Props = VariantProps<typeof modalDialogVariants> & {
  /** モーダルの表示状態 */
  isOpen: boolean;
  /** モーダルを閉じる際のコールバック関数 */
  onClose: () => void;
  /** モーダルのタイトル */
  title: string;
  /** タイトル下に表示する説明文（オプション） */
  description: string;
  /** モーダルのメインコンテンツ */
  children: ReactNode;
  /** 確認ボタン押下時のコールバック関数（オプション） */
  onConfirm: () => void;
  /** キャンセルボタン押下時のコールバック関数（オプション） */
  onCancel?: () => void;
  /** 確認ボタンのテキスト */
  confirmText?: string;
  /** キャンセルボタンのテキスト */
  cancelText?: string;
  /** 確認ボタンを disabled にするか */
  isConfirmDisabled?: boolean;
  /** モーダルのサイズバリアント */
  variant?: 'mdCenter' | 'md' | 'lg' | 'xl';
  /**
   * 確認ボタンの色バリアント
   * - 'primary'（デフォルト）: 通常の確認操作に使用
   * - 'destructive': 削除・取消など破壊的操作の確認に使用
   */
  confirmVariant?: 'primary' | 'destructive';
};

/**
 * カスタマイズ可能なモーダルダイアログコンポーネント
 * 4つのサイズバリアント（mdCenter, md, lg, xl）を提供し、
 * タイトル、説明文、確認・キャンセルボタンを含む汎用的なダイアログを表示します。
 */
export const ModalDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  onConfirm,
  onCancel,
  confirmText = '確認',
  cancelText = 'キャンセル',
  isConfirmDisabled,
  variant = 'md',
  confirmVariant = 'primary',
}) => {
  const element = document.getElementById(CREATE_PORTAL_ID);
  useDisableModalBackScroll({ isOpen });
  useHandleKeyDown({ isOpen, onClose });

  if (!isOpen || !element) return null;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();

      return;
    }
    onClose();
  };

  const className = modalDialogVariants({ variant });

  return (
    <div className={styles.overlay}>
      <div
        role='dialog'
        aria-modal='true'
        onClick={e => e.stopPropagation()}
        className={styles.innerWrapper}
      >
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            <IconButton
              variant='ghost'
              size='md'
              icon={<CrossIcon size='xs' />}
              onClick={onClose}
              aria-label='モーダルを閉じる'
              className={styles.closeButton}
            />
            <div className={className}>
              <div className={styles.header}>
                <div className={styles.title}>{title}</div>
                {description && <div className={styles.description}>{description}</div>}
              </div>
              <div className={styles.contentBody}>{children}</div>
              <div className={styles.actions}>
                <Button variant='outline' onClick={handleCancel}>
                  {cancelText}
                </Button>
                {onConfirm && (
                  <Button
                    variant={confirmVariant}
                    onClick={handleConfirm}
                    disabled={isConfirmDisabled}
                  >
                    {confirmText}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
